import { useEffect, useRef, useState } from "react";
import {
  ComposedChart, Line, Area, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import { DownloadSimple, TrendUp, ChartLine, Info, Image as ImageIcon } from "@phosphor-icons/react";
import { getAirTemperature, AIR_EXPORT_URL } from "@/lib/api";
import { CHART } from "@/lib/chartTheme";
import { exportSvgAsPng } from "@/lib/exportPng";

function strength(r) {
  if (r == null) return "—";
  const a = Math.abs(r);
  const s = a < 0.3 ? "débil" : a < 0.6 ? "moderada" : a < 0.8 ? "fuerte" : "muy fuerte";
  return `${s} y ${r < 0 ? "inversa" : "directa"}`;
}

function SeriesTip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#ffffff] border border-[#e5e7eb] px-3 py-2 rounded-md text-xs mono space-y-0.5">
      <div className="text-[#334155]">Año {label}</div>
      {payload.map((p) => (
        <div key={p.dataKey} style={{ color: p.color }}>{p.name}: {p.value != null ? `${p.value} °C` : "sin dato"}</div>
      ))}
    </div>
  );
}

export default function AirTempView() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const seriesRef = useRef(null);
  const scatterRef = useRef(null);

  useEffect(() => {
    setLoading(true);
    getAirTemperature().then(setData).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const t = data?.trends;
  const v = data?.validation;
  const lva = data?.lst_vs_air;
  const scatterData = (data?.series || []).filter((s) => s.lst != null && s.complementada != null);

  return (
    <div className="flex-1 overflow-y-auto p-6 lg:p-8" data-testid="airtemp-view">
      <div className="max-w-6xl mx-auto space-y-6 fade-up">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h2 className="plex text-2xl font-semibold tracking-tight">Temperatura del aire: INDOMET oficial + ERA5-Land</h2>
            <p className="text-sm text-[#334155] mt-1 max-w-3xl">
              Serie térmica anual de Constanza (2000–2024) con datos oficiales del <span className="text-[#1f2937]">INDOMET</span>
              {" "}(estación, temperatura del aire a 2 m), reanálisis <span className="text-[#1f2937]">ERA5-Land</span> y, como referencia,
              la <span className="text-orange-300">LST satelital</span> (temperatura de superficie, Landsat 30 m).
            </p>
          </div>
          <div className="flex items-center gap-2">
            <a href={AIR_EXPORT_URL} data-testid="airtemp-export-csv"
              className="flex items-center gap-2 text-xs px-3 py-2 rounded-md bg-[#4a9b3c] hover:bg-[#3f8e33] text-white transition-colors">
              <DownloadSimple size={15} weight="bold" /> Exportar CSV
            </a>
            <button onClick={() => exportSvgAsPng(seriesRef.current, "constanza_temperatura_aire.png")}
              data-testid="airtemp-export-png"
              className="flex items-center gap-2 text-xs px-3 py-2 rounded-md border border-[#e5e7eb] text-[#1f2937] hover:bg-[#eef7ed] transition-colors">
              <ImageIcon size={15} /> Figura PNG
            </button>
          </div>
        </div>

        {loading && <div className="text-sm mono text-[#334155] animate-pulse">Cargando serie térmica…</div>}

        {data && !loading && (
          <>
            {/* Tendencias */}
            <div className="grid md:grid-cols-3 gap-4">
              <TrendCard label="Tendencia INDOMET (oficial)" value={t.indomet_decade} unit="°C/década"
                sub={`R² = ${t.indomet_r2.toFixed(2)} · p = ${t.indomet_p.toExponential(1)} (significativa)`}
                accent={CHART.ndvi} testId="airtemp-trend-indomet" />
              <TrendCard label="Tendencia ERA5-Land" value={t.era5_decade} unit="°C/década"
                sub={`R² = ${t.era5_r2.toFixed(2)} · p = ${t.era5_p.toExponential(1)}`}
                accent={CHART.navy} testId="airtemp-trend-era5" />
              <div className="border border-[#e5e7eb] bg-[#f8faf9] rounded-lg p-4" data-testid="airtemp-conclusion">
                <div className="flex items-center gap-1.5 label-caps"><TrendUp size={13} /> Conclusión</div>
                <p className="text-[13px] text-[#1f2937] mt-2 leading-relaxed">{t.conclusion}</p>
              </div>
            </div>

            {/* Gráfico de serie superpuesta */}
            <div className="border border-[#e5e7eb] bg-[#ffffff] rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <ChartLine size={16} className="text-[#3f8e33]" />
                <span className="plex text-sm font-medium">Evolución anual — aire (eje izq.) vs superficie satelital (eje der.)</span>
              </div>
              <div className="h-[360px]" ref={seriesRef} data-testid="airtemp-series-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={data.series} margin={{ top: 10, right: 8, left: -8, bottom: 16 }}>
                    <defs>
                      <linearGradient id="gAirIndomet" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={CHART.ndviDark} stopOpacity={0.18} />
                        <stop offset="100%" stopColor={CHART.ndviDark} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke={CHART.grid} />
                    <XAxis dataKey="year" tick={{ fill: "#94a3b8", fontSize: 10 }} axisLine={{ stroke: "#e5e7eb" }} tickLine={false} minTickGap={18} />
                    <YAxis yAxisId="air" orientation="left" domain={["auto", "auto"]}
                      tick={{ fill: "#94a3b8", fontSize: 10 }} axisLine={false} tickLine={false}
                      label={{ value: "Aire (°C)", angle: -90, position: "insideLeft", fill: "#64748b", fontSize: 10 }} />
                    <YAxis yAxisId="lst" orientation="right" domain={["auto", "auto"]}
                      tick={{ fill: CHART.lst, fontSize: 10 }} axisLine={false} tickLine={false}
                      label={{ value: "Superficie LST (°C)", angle: 90, position: "insideRight", fill: "#c2622a", fontSize: 10 }} />
                    <Tooltip content={<SeriesTip />} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Area yAxisId="air" type="monotone" dataKey="indomet" name="INDOMET (observado)" stroke={CHART.ndvi}
                      fill="url(#gAirIndomet)" strokeWidth={2.2} dot={{ r: 2.5 }} connectNulls={false} isAnimationActive={false} activeDot={{ r: 4, strokeWidth: 0 }} />
                    <Line yAxisId="air" type="monotone" dataKey="era5" name="ERA5-Land" stroke={CHART.navy}
                      strokeWidth={1.6} dot={false} isAnimationActive={false} />
                    <Line yAxisId="lst" type="monotone" dataKey="lst" name="LST satelital (superficie)" stroke={CHART.lst}
                      strokeWidth={1.8} strokeDasharray="5 3" dot={false} isAnimationActive={false} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
              <p className="text-[11px] text-[#475569] mt-2">
                La serie INDOMET deja en blanco 2018–2022 (sin registro oficial). El vacío se complementa con ERA5-Land ajustado
                (ver CSV). La LST de superficie usa una escala distinta porque es una magnitud física diferente a la del aire.
              </p>
            </div>

            {/* Validación + correlación LST vs aire */}
            <div className="grid lg:grid-cols-2 gap-6">
              <div className="border border-[#e5e7eb] bg-[#ffffff] rounded-lg p-4" data-testid="airtemp-validation">
                <div className="plex text-sm font-medium mb-3">Validación INDOMET ↔ ERA5-Land (20 años coincidentes)</div>
                <div className="grid grid-cols-2 gap-3">
                  <Metric label="Correlación (Pearson r)" value={v.pearson} accent={CHART.ndvi} />
                  <Metric label="R² de validación" value={v.r2} accent={CHART.ndvi} />
                  <Metric label="RMSE" value={v.rmse} unit="°C" accent={CHART.lst} />
                  <Metric label="Sesgo ERA5 − INDOMET" value={v.sesgo} unit="°C" accent={CHART.navy} />
                </div>
                <div className="mt-3 text-[11px] text-[#334155] border-t border-[#e5e7eb] pt-2">
                  <div className="mono text-[#1f2937]">{v.ecuacion}</div>
                  <p className="mt-1">{v.interpretacion}</p>
                </div>
              </div>

              <div className="border border-[#e5e7eb] bg-[#ffffff] rounded-lg p-4">
                <div className="plex text-sm font-medium mb-3">LST (superficie) vs temperatura del aire</div>
                <div className="border border-[#e5e7eb] bg-[#f8faf9] rounded-lg p-3 mb-3" data-testid="airtemp-lst-vs-air">
                  <div className="label-caps">Correlación anual (n = {lva?.n})</div>
                  <div className="mono text-3xl font-semibold mt-1" style={{ color: lva?.r == null ? "#94a3b8" : lva.r < 0 ? CHART.navy : CHART.ndvi }}>
                    r = {lva?.r ?? "…"}
                  </div>
                  <div className="text-xs text-[#334155] mt-0.5">Correlación {strength(lva?.r)}</div>
                </div>
                <div className="h-[190px]" ref={scatterRef} data-testid="airtemp-scatter">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={scatterData} margin={{ top: 6, right: 12, left: -10, bottom: 12 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={CHART.grid} />
                      <XAxis type="number" dataKey="lst" name="LST" unit="°C" domain={["auto", "auto"]}
                        tick={{ fill: "#94a3b8", fontSize: 10 }} axisLine={{ stroke: "#e5e7eb" }} tickLine={false}
                        label={{ value: "LST superficie (°C)", position: "bottom", fill: "#64748b", fontSize: 10 }} />
                      <YAxis type="number" dataKey="complementada" domain={["auto", "auto"]}
                        tick={{ fill: "#94a3b8", fontSize: 10 }} axisLine={false} tickLine={false}
                        label={{ value: "Aire (°C)", angle: -90, position: "insideLeft", fill: "#64748b", fontSize: 10 }} />
                      <Tooltip contentStyle={{ background: "#ffffff", border: "1px solid #e5e7eb", borderRadius: 6, fontSize: 11 }} />
                      <Scatter dataKey="complementada" fill={CHART.ndvi} isAnimationActive={false} name="Año" />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            {/* Fuentes y notas */}
            <div className="border border-[#e5e7eb] bg-[#f8faf9] rounded-lg p-4" data-testid="airtemp-sources">
              <div className="flex items-center gap-1.5 plex font-medium mb-2 text-[#1a2318]"><Info size={15} /> Fuentes y notas metodológicas</div>
              <ul className="space-y-2 text-[13px] text-[#1f2937]">
                {data.sources.map((s, i) => (
                  <li key={i}><span className="text-[#3f8e33] font-medium">{s.fuente}:</span> {s.descripcion} <span className="text-[#475569]">{s.nota}</span></li>
                ))}
              </ul>
              <ul className="mt-3 space-y-1 text-[12px] text-[#475569] border-t border-[#e5e7eb] pt-2">
                {data.notes.map((n, i) => <li key={i}>• {n}</li>)}
              </ul>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function TrendCard({ label, value, unit, sub, accent, testId }) {
  return (
    <div className="border border-[#e5e7eb] bg-[#f8faf9] rounded-lg p-4" data-testid={testId}>
      <div className="flex items-center gap-1.5 label-caps"><TrendUp size={13} /> {label}</div>
      <div className="mono text-3xl font-semibold mt-1" style={{ color: accent }}>
        +{value}<span className="text-sm text-[#475569] ml-1">{unit}</span>
      </div>
      <div className="text-[11px] text-[#334155] mt-1">{sub}</div>
    </div>
  );
}

function Metric({ label, value, unit = "", accent }) {
  return (
    <div className="border border-[#e5e7eb] p-2.5 bg-[#f8faf9]">
      <div className="label-caps">{label}</div>
      <div className="mono text-lg font-semibold" style={{ color: accent }}>
        {value != null ? value : "—"}<span className="text-[10px] text-[#475569] ml-0.5">{unit}</span>
      </div>
    </div>
  );
}
